import {
  AnalyticsVisualization, AnalyticsResponse, AnalyticsDataPoint, QueryFilter,
  DataSetContext, VisualizationType, AnalyticsDataQuery, AnalyticsRequest, IAnalyticsService, AnalyticsConfig,
  TableColumn
} from '../types/analytics.types';
import { BackendAnalyticsService } from './backend-analytics.service';
import { PlaceholderAnalyticsService } from './placeholder-analytics.service';
import { TimeGroupingService } from './time-grouping.service';
import { DataSetManager } from './data-set.manager';
import { DateConstantsService } from './date-constants.service';

/**
 * Enhanced Smart Data Manager with Data Set Framework Support
 * 
 * Provides intelligent data loading with hierarchical data set resolution:
 * 1. Check for component-level dataset
 * 2. Check for tab-level dataset  
 * 3. Check for dashboard-level dataset
 * 4. Fallback to individual API calls (legacy mode)
 * 
 * Key Features:
 * - Hierarchical data set resolution
 * - Smart caching and performance optimization
 * - Backward compatibility with existing visualizations
 * - Client-side filtering and time grouping
 */
export class SmartDataManager {
  private cache: Map<string, { data: AnalyticsResponse; timestamp: number }> = new Map();
  private backendService: BackendAnalyticsService;
  private placeholderService: PlaceholderAnalyticsService;
  private dataSetManager: DataSetManager;
  private activeService: IAnalyticsService;
  private readonly CACHE_TTL = 900000; // 15 minutes
  private useMockData: boolean = true; // Track mock data preference

  // Legacy compatibility properties
  private fullDataCache: Map<string, AnalyticsDataPoint[]> = new Map();
  private lastServerQueries: Map<string, QueryFilter[]> = new Map();

  // Track live data loads per tab to prevent duplicate calls
  private liveDataLoadedTabs: Set<string> = new Set();

  // Cache for shared source data to prevent duplicate calls within same refresh cycle
  private sourceDataCache: Map<string, AnalyticsDataPoint[]> = new Map();

  // PERFORMANCE: Cache for hierarchical table results to avoid rebuilding
  private hierarchyCache: Map<string, { result: AnalyticsDataPoint[]; timestamp: number }> = new Map();

  // Data Set Framework support
  private dataSetContext?: DataSetContext;
  private currentTabId?: string;

  constructor(
    analyticsService?: IAnalyticsService,
    apiConfig?: {
      baseUrl: string;
      appKey: string;
      dashboardConfig?: AnalyticsConfig;
      getAuthToken?: () => Promise<string>;
    }
  ) {
    this.backendService = apiConfig
      ? new BackendAnalyticsService(apiConfig.baseUrl, apiConfig.appKey, apiConfig.getAuthToken)
      : new BackendAnalyticsService('', '');

    // Set dashboard configuration if provided
    if (apiConfig?.dashboardConfig) {
      this.backendService.setDashboardConfig(apiConfig.dashboardConfig);
    }

    this.placeholderService = new PlaceholderAnalyticsService();

    // Use provided service or default to backend service
    const serviceToUse = analyticsService || this.backendService;
    this.activeService = serviceToUse;

    // Create a simplified IAnalyticsService wrapper for DataSetManager
    this.dataSetManager = new DataSetManager({
      processAnalytics: async (request: AnalyticsRequest) => {
        // Convert AnalyticsRequest to AnalyticsDataQuery format for compatibility
        // Use request.limit if provided, otherwise no limit (needed for filter generation)
        const query = {
          configId: request.configId,
          metrics: request.metrics || [],
          dimensions: request.dimensions,
          groupBy: [],
          filters: (request.filters || []).map(f => ({
            field: f.filterId,
            operator: 'eq' as const,
            value: f.value
          })),
          limit: (request as any).limit  // Pass through limit if provided, undefined otherwise
        };
        return serviceToUse.processAnalytics(query);
      },
      getConfig: async (configId: string) => {
        return serviceToUse.getConfig(configId);
      }
    } as any);
  }

  /**
   * Initialize with Data Set Context (NEW)
   */
  initializeDataSets(context: DataSetContext, activeTabId?: string): void {
    this.dataSetContext = context;
    this.currentTabId = activeTabId;
    this.dataSetManager.registerDataSets(context);
  }

  /**
   * Update Data Set Context after virtual datasets are created (NEW)
   */
  updateDataSetContext(context: DataSetContext): void {

    this.dataSetContext = context;
  }

  /**
   * Register dashboard configuration for unified mock data (NEW)
   */
  registerDashboardConfig(dashboardConfig: AnalyticsConfig): void {
    console.log(`🔧 [SmartDataManager] registerDashboardConfig called`);
    console.log(`   Dashboard ID: ${dashboardConfig.id}`);
    console.log(`   dataSource: ${dashboardConfig.dataSource}`);
    console.log(`   Has dataSets:`, !!dashboardConfig.dataSets);
    console.log(`   Has mockDataConfig:`, !!dashboardConfig.mockDataConfig);

    this.dataSetManager.registerDashboardConfig(dashboardConfig);

    // ✅ For dashboards with mockDataConfig, set up dataSetContext for both mock and live data modes
    // This enables the unified dataset endpoint regardless of the useMockData toggle
    if (dashboardConfig.dataSource === 'mock' && dashboardConfig.mockDataConfig) {

      // Generate cache key for cache invalidation when config changes
      let configKey = '';
      const configWithTimestamps = dashboardConfig as any;

      // Check for preview mode cache key first (from analytics.dashboard.resource.tsx)
      if (configWithTimestamps._cacheKey) {
        configKey = configWithTimestamps._cacheKey;

      } else if (configWithTimestamps.updatedAt) {
        const timestamp = new Date(configWithTimestamps.updatedAt).getTime();
        configKey = `-config-${timestamp}`;
      } else if (configWithTimestamps.createdAt) {
        const timestamp = new Date(configWithTimestamps.createdAt).getTime();
        configKey = `-config-${timestamp}`;
      }

      // Determine mode suffix to prevent cache collisions between mock and live data
      // Preview mode (design mode in admin portal) should use different IDs than live mode
      const isPreviewMode = (dashboardConfig as any)._previewMode === true;
      const modeSuffix = isPreviewMode ? '-preview' : (this.useMockData ? '-mock' : '-live');

      // Use cache-busted dataset ID with mode differentiation for proper cache isolation
      const virtualDataSetId = `${dashboardConfig.id}${configKey}${modeSuffix}-dataset`;


      // Create the dashboard context with the virtual dataset info
      const dashboardContext: DataSetContext = {
        dashboard: {
          id: virtualDataSetId,
          name: `${dashboardConfig.name} Dataset`,
          description: `Generated dataset for ${dashboardConfig.name}`,
          scope: 'dashboard',
          source: {
            endpoint: `/analytics/datasets/${dashboardConfig.id}-dataset`, // Clean endpoint for API routing
            baseFilters: [],
            // Include mode in cache key to prevent mock/live collisions
            cacheKey: `${dashboardConfig.id}${configKey}${modeSuffix}-unified-v3` // Cache-busted key with mode differentiation
          },
          refreshStrategy: 'on_load',
          mockDataConfig: dashboardConfig.mockDataConfig
        }
      };

      this.updateDataSetContext(dashboardContext);
    }

    // Note: Static datasets are already registered via dataSetManager.registerDashboardConfig above
    // The dataSetContext is already set in the if block above (lines 133-174)
    // No need to register again or update context here
  }

  /**
   * Update active tab for data set resolution (NEW)
   */
  setActiveTab(tabId: string): void {
    // Clear source cache when switching tabs in live data mode to allow fresh loads
    if (!this.useMockData) {
      this.sourceDataCache.clear();
    }
    this.currentTabId = tabId;
  }

  /**
   * Set mock data preference (NEW)
   */
  setUseMockData(useMockData: boolean): void {
    this.useMockData = useMockData;


    // Pass the preference to DataSetManager
    this.dataSetManager.setForceLiveData(!useMockData);

    // Clear live data load tracking when switching data sources
    this.liveDataLoadedTabs.clear();

    // Clear source data cache when switching data sources
    this.sourceDataCache.clear();
  }

  /**
   * Enhanced data loading with Data Set Framework support
   */
  async loadVisualizationData(
    visualization: AnalyticsVisualization,
    filters: Record<string, any>,
    forceRefresh = false,
    requiredFields?: string[]
  ): Promise<AnalyticsResponse> {
    const loadStart = performance.now();
    console.log(`⏱️ [PERF] ========== loadVisualizationData START: ${visualization.id} (${visualization.type}) ==========`);

    // Strategy 1: Try Data Set Framework (NEW)
    if (this.dataSetContext && visualization.dataSet) {
      try {
        const dataSetData = await this.loadFromDataSet(visualization, filters);
        if (dataSetData.length > 0) {
          return {
            configId: visualization.dataQuery?.configId || 'dataset',
            data: dataSetData,
            metadata: {
              totalRecords: dataSetData.length,
              processingTime: 0,
              cacheHit: false,
              dataSource: 'dataset',
              lastRefreshed: new Date()
            }
          };
        }
      } catch (error) {
        console.warn(`⚠️ [SmartDataManager] Dataset loading failed, falling back:`, error);
      }
    }

    // Strategy 1.5: For mock data dashboards, auto-assign visualizations to unified dataset
    if (this.dataSetContext?.dashboard && !visualization.dataSet && !visualization.dataQuery) {
      const isMockDataDashboard = this.dataSetContext.dashboard.mockDataConfig;

      if (isMockDataDashboard) {
        try {
          // Create a temporary dataSet reference to the dashboard dataset
          const tempDataSetRef = {
            dataSetId: this.dataSetContext.dashboard.id,
            scope: 'dashboard' as const,
            fallbackMode: 'api' as const
          };

          const visualizationWithDataSet = { ...visualization, dataSet: tempDataSetRef };
          const dataSetData = await this.loadFromDataSet(visualizationWithDataSet, filters);
          if (dataSetData.length > 0) {
            return {
              configId: 'unified-dataset',
              data: dataSetData,
              metadata: {
                totalRecords: dataSetData.length,
                processingTime: 0,
                cacheHit: false,
                dataSource: 'unified-mock-dataset',
                lastRefreshed: new Date()
              }
            };
          }
        } catch (error) {
          console.warn(`⚠️ [SmartDataManager] Unified dataset loading failed for ${visualization.id}, using exampleResponse:`, error);
        }
      }
    }

    // Strategy 2: For live mode and dataset-enabled dashboards, avoid legacy per-visualization calls.
    // Fallback to legacy only if there is no dataset context.
    if (this.dataSetContext) {
      const data = await this.loadFromDataSet(visualization, filters);
      return {
        configId: visualization.dataQuery?.configId || 'dataset',
        data,
        metadata: {
          totalRecords: data.length,
          processingTime: 0,
          cacheHit: false,
          dataSource: 'dataset',
          lastRefreshed: new Date()
        }
      };
    }

    const result = await this.loadVisualizationDataLegacy(visualization, filters, forceRefresh);

    const loadEnd = performance.now();
    console.log(`⏱️ [PERF] ========== loadVisualizationData COMPLETE: ${visualization.id} in ${(loadEnd - loadStart).toFixed(2)}ms ==========`);

    return result;
  }

  /**
   * Load data using Data Set Framework (NEW)
   */
  private async loadFromDataSet(
    visualization: AnalyticsVisualization,
    filters: Record<string, any>,
    requiredFields?: string[]
  ): Promise<AnalyticsDataPoint[]> {

    const dashboardDataSetId = this.dataSetContext?.dashboard?.id;

    // For performance cards with sourceVisualizationId, get processed data from source visualization
    const cardConfig = visualization.config as any;
    if (visualization.type === 'performance-card' &&
      cardConfig?.mode === 'calculated' &&
      cardConfig?.sourceVisualizationId) {
      const sourceVizId = cardConfig.sourceVisualizationId;
      console.log(`📊 [PerformanceCard] Loading source data from ${sourceVizId} for ${visualization.id}`);

      try {
        // Get raw data from source visualization (will be processed in processDataSetData)
        const sourceRawData = await this.dataSetManager.getVisualizationData(
          sourceVizId,
          visualization.dataSet, // Use same dataset reference
          filters,
          this.currentTabId,
          dashboardDataSetId,
          undefined // Source viz config not available here, but getVisualizationData should handle it
        );

        if (sourceRawData && sourceRawData.length > 0) {
          // Process the source visualization's data to get hierarchical structure
          // We need to create a mock source visualization object for processing
          // The key is that hierarchical tables include all registers in their processed data
          // DON'T pass the performance card's config (rankingType, etc.) to avoid premature ranking
          const sourceProcessedData = this.processDataSetData(
            {
              id: sourceVizId,
              type: 'table',
              dataSet: visualization.dataSet,
              dataQuery: visualization.dataQuery,
              config: {} // Empty config - no ranking should happen on the table
            } as AnalyticsVisualization,
            sourceRawData,
            filters
          );

          if (sourceProcessedData && sourceProcessedData.length > 0) {
            console.log(`📊 [PerformanceCard] Using ${sourceProcessedData.length} processed rows from ${sourceVizId}`);

            // Apply ranking logic for performance cards with rankingType
            if (cardConfig.rankingType) {
              const rankingType = cardConfig.rankingType;
              const metricField = cardConfig.metricField;
              const displayField = cardConfig.displayField || 'location';
              const formula = cardConfig.formula as string | undefined;
              const hasFormula = !!formula && typeof formula === 'string';
              let formulaDeps: string[] = [];
              if (hasFormula && formula) {
                const matches = formula.match(/\{\{(\w+)\}\}/g) || [];
                formulaDeps = matches.map((m: string) => m.replace(/\{\{|\}\}/g, ''));
              }

              const getMetricValue = (row: any): number => {
                if (hasFormula && formulaDeps.length > 0) {
                  let expression = formula;
                  formulaDeps.forEach((dep: string) => {
                    const val = Number(row[dep]) || 0;
                    expression = expression.replace(new RegExp(`\\{\\{${dep}\\}\\}`, 'g'), String(val));
                  });
                  try {
                    const sanitized = expression.replace(/[^0-9+\-*/().  ]/g, '');
                    const result = Function('"use strict"; return (' + sanitized + ')')();
                    return isFinite(result) ? result : 0;
                  } catch {
                    return 0;
                  }
                }
                return Number(row[metricField]) || 0;
              };

              // Filter to only leaf nodes (e.g., registers, zones, not parent location rows)
              const leafNodes = sourceProcessedData.filter((row: any) =>
                row[displayField] && !row.hasChildren
              );

              if (leafNodes.length > 0) {
                // Sort by the metric (using formula-computed value when present)
                const sorted = [...leafNodes].sort((a, b) => {
                  const aVal = getMetricValue(a);
                  const bVal = getMetricValue(b);
                  return rankingType === 'highest' ? bVal - aVal : aVal - bVal;
                });

                const topRow = sorted[0];
                // When using formula, inject the computed value into the result for display
                if (hasFormula && metricField) {
                  const computedValue = getMetricValue(topRow);
                  return [{ ...topRow, [metricField]: computedValue }];
                }
                return [topRow];
              }
            }

            return sourceProcessedData;
          }
        }
      } catch (error) {
        console.warn(`⚠️ [PerformanceCard] Failed to load source data from ${sourceVizId}, falling back to raw data:`, error);
      }
    }

    const rawData = await this.dataSetManager.getVisualizationData(
      visualization.id,
      visualization.dataSet,  // ← Should be {dataSetId: "population-data"}
      filters,
      this.currentTabId,
      dashboardDataSetId,  // ← This is "top5-countries-population-20202024-dataset"
      visualization,
      requiredFields
    );

    // Apply visualization-specific processing (this includes client-side filtering)
    const processedData = this.processDataSetData(visualization, rawData, filters);

    return processedData;
  }

  /**
   * Process dataset data for specific visualization (NEW)
   */
  private processDataSetData(
    visualization: AnalyticsVisualization,
    rawData: AnalyticsDataPoint[],
    filters: Record<string, any>
  ): AnalyticsDataPoint[] {
    const processStart = performance.now();
    console.log(`⏱️ [PERF] Starting processDataSetData for ${visualization.id} (${visualization.type}): ${rawData.length} records`);

    let processedData = [...rawData];

    // Special processing for calculated charts
    if (visualization.type === 'chart' && (visualization.config as any)?.mode === 'calculated') {
      const chartConfig = visualization.config as any;

      // 🚀 GENERIC: Apply filter-to-metric mapping (configuration-driven)
      // Rule: If filter values match chart metrics, apply as metric filtering instead of record filtering
      Object.entries(filters).forEach(([filterKey, filterValue]) => {
        // Skip date filters and non-array filters
        if (filterKey.includes('date') || !Array.isArray(filterValue)) {
          return;
        }

        const originalMetrics = chartConfig.metrics || [];

        // Check if any filter values match chart metrics
        const matchingMetrics = filterValue.filter((value: string) =>
          originalMetrics.includes(value)
        );

        if (matchingMetrics.length > 0) {
          // Apply metric filtering: only show metrics that match selected filter values
          const filteredMetrics = originalMetrics.filter((metric: string) =>
            filterValue.includes(metric)
          );



          // Update the chart config to use filtered metrics
          chartConfig.metrics = filteredMetrics;

          // Also update dataQuery.metrics if it exists
          if (visualization.dataQuery?.metrics) {
            visualization.dataQuery.metrics = filteredMetrics;
          }
        }
      });

      // 🔧 DEBUG: Log the condition components
      const dataQueryGroupBy = visualization.dataQuery?.groupBy || [];
      const includesDate = dataQueryGroupBy.some(field => {
        const fieldName = String(field).valueOf();
        return fieldName === 'date' || fieldName === 'time';
      });


      // 🔧 FIXED: Use time-based aggregation for charts with date grouping (handle String objects)
      if (includesDate) {

        // 🚀 FIX: Use filtered date range from the applied filters with fallback
        let dateFilter = filters[chartConfig.dateFilterId];

        // 🔧 FALLBACK: If the specific date filter doesn't exist, use any available date filter
        if (!dateFilter && chartConfig.dateFilterId) {
          const availableDateFilters = Object.keys(filters).filter(key => key.includes('date'));
          if (availableDateFilters.length > 0) {
            const fallbackFilterId = availableDateFilters[0];
            dateFilter = filters[fallbackFilterId];

          }
        }


        // Resolve date constants before creating Date objects
        const resolveDate = (dateValue: any): Date => {
          if (!dateValue) return new Date();
          if (dateValue instanceof Date) return dateValue;
          if (typeof dateValue === 'string') {
            if (DateConstantsService.isDateConstant(dateValue)) {
              const resolvedDateString = DateConstantsService.resolveDateConstant(dateValue);
              return new Date(resolvedDateString);
            }
            return new Date(dateValue);
          }
          return new Date(dateValue);
        };

        const minDate = dateFilter?.start
          ? resolveDate(dateFilter.start)
          : new Date(Math.min(...processedData.map(r => new Date(r.date).getTime())));
        const maxDate = dateFilter?.end
          ? resolveDate(dateFilter.end)
          : new Date(Math.max(...processedData.map(r => new Date(r.date).getTime())));

        const metrics = chartConfig.metrics || ['total_sales'];
        const aggregation = chartConfig.aggregation || 'sum';
        // 🔧 CRITICAL FIX: Use dataQuery.groupBy (NOT config.groupBy) for dimensions, handle String objects
        const dataQueryGroupByRaw = visualization.dataQuery?.groupBy || [];
        const dataQueryGroupBy = dataQueryGroupByRaw.map(field => String(field).valueOf());
        const additionalGroupBy = dataQueryGroupBy.filter(field => field !== 'date' && field !== 'time'); // Remove 'date'/'time' from groupBy for TimeGroupingService
        const timeGroupingRules = chartConfig.timeAxisRules;


        const timeGroupedResult = TimeGroupingService.processChartDataWithDimensions(
          processedData,
          minDate,
          maxDate,
          metrics,
          aggregation,
          additionalGroupBy.length > 0 ? additionalGroupBy : undefined,
          timeGroupingRules
        );

        processedData = timeGroupedResult.data;

        // Apply series filtering based on chart configuration
        if (additionalGroupBy && additionalGroupBy.length > 0) {
          const groupByField = additionalGroupBy[0];

          // Only apply location filtering for charts that explicitly configure locationFilterId
          if ((groupByField === 'location' || groupByField.includes('location')) &&
            chartConfig.locationFilterId) {

            // Check if the configured location filter has selections
            const locationFilterValue = filters[chartConfig.locationFilterId];

            if (Array.isArray(locationFilterValue) && locationFilterValue.length > 0) {
              // Filter chart data to only show selected locations
              const frameworkFields = ['date', 'dateFormatted', 'timeKey', 'recordCount'];
              const sampleRow = processedData[0];
              const allSeriesFields = Object.keys(sampleRow || {}).filter(field =>
                !frameworkFields.includes(field) && typeof (sampleRow?.[field]) === 'number'
              );

              // Keep only series fields that match selected locations
              const selectedSeriesFields = allSeriesFields.filter(field =>
                locationFilterValue.some(location => field.includes(location))
              );

              // Filter the data to keep only framework fields + selected location series
              const fieldsToKeep = [...frameworkFields, ...selectedSeriesFields];
              processedData = processedData.map(row => {
                const filteredRow: any = {};
                fieldsToKeep.forEach(field => {
                  if (row.hasOwnProperty(field)) {
                    filteredRow[field] = row[field];
                  }
                });
                return filteredRow;
              });
            } else {
              // No locations selected in the configured filter: show empty chart
              const frameworkFields = ['date', 'dateFormatted', 'timeKey', 'recordCount'];
              processedData = processedData.map(row => {
                const filteredRow: any = {};
                frameworkFields.forEach(field => {
                  if (row.hasOwnProperty(field)) {
                    filteredRow[field] = row[field];
                  }
                });
                return filteredRow;
              });
            }
          } else {
            // For charts without locationFilterId OR non-location charts, use maxSeries limit
            const maxSeries = chartConfig.maxSeries || 5;

            // Use config values directly - clean OOP style
            const splitLocations = chartConfig.splitLocations || false;
            const showEnterprise = chartConfig.showEnterprise || false;
            const showTopAndBottom = chartConfig.showTopAndBottom || false;
            const topBottomCount = chartConfig.topBottomCount || 1;

            processedData = this.limitChartSeries(processedData, groupByField, maxSeries, splitLocations, showEnterprise, chartConfig.metrics?.[0] || 'value', showTopAndBottom, topBottomCount);
          }
        }
      } else if (chartConfig.groupBy && chartConfig.groupBy.length > 0) {
        // Category-based chart aggregation (fallback for non-time charts)
        const metrics = chartConfig.metrics || [chartConfig.yAxis?.field || 'count'];
        const aggregation = chartConfig.aggregation || 'sum';
        const groupByFields = chartConfig.groupBy;


        processedData = this.aggregateByCategory(processedData, groupByFields, metrics, aggregation);
      }
    }

    // 🔧 FIXED: Only aggregate tables that explicitly need aggregation (not hierarchical detail tables)
    if (visualization.type === 'table' &&
      visualization.dataQuery?.groupBy &&
      visualization.dataQuery.groupBy.length > 0 &&
      !visualization.dataQuery?.hierarchyLevels) { // Don't aggregate hierarchical tables
      const metrics = visualization.dataQuery.metrics || [];
      // 🔧 FIXED: Handle String objects in groupBy fields
      const groupByFields = (visualization.dataQuery.groupBy || []).map(field => String(field).valueOf());



      processedData = this.aggregateTableByCategory(processedData, groupByFields, metrics);
    }

    // Special processing for calculated performance cards
    if (visualization.type === 'performance-card' && (visualization.config as any)?.mode === 'calculated') {
      const cardConfig = visualization.config as any;
      const metricField = cardConfig.metricField || 'total_sales';
      // 🔧 FIXED: Handle String objects and convert to primitive string
      let groupByFieldRaw = visualization.dataQuery?.groupBy?.[0];
      if (!groupByFieldRaw) {
        // Fallback for configs that implicitly mean location grouping
        groupByFieldRaw = 'location';
      }
      const groupByField = String(groupByFieldRaw).valueOf();
      const isLowest = cardConfig.rankingType === 'lowest';

      // Apply dataQuery.filters BEFORE aggregation (critical for order type filtering)
      let filteredData = processedData;
      if (visualization.dataQuery?.filters && Array.isArray(visualization.dataQuery.filters)) {
        const toCamel = (s: string) => s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
        const toSnake = (s: string) => s.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
        const normalize = (v: any) => {
          if (v === null || v === undefined) return v;
          return String(v).toLowerCase().replace(/[\s_-]+/g, '');
        };
        const getRowFieldValue = (row: any, fieldName: string): any => {
          if (row.hasOwnProperty(fieldName)) return row[fieldName];
          const alt1 = toCamel(fieldName);
          if (row.hasOwnProperty(alt1)) return row[alt1];
          const alt2 = toSnake(fieldName);
          if (row.hasOwnProperty(alt2)) return row[alt2];
          // Last resort: case-insensitive key match
          const key = Object.keys(row).find(k => k.toLowerCase() === fieldName.toLowerCase());
          return key ? row[key] : undefined;
        };
        visualization.dataQuery.filters.forEach(filter => {
          const { field, operator, value } = filter;
          filteredData = filteredData.filter(row => {
            const rowValueRaw = getRowFieldValue(row, field);
            if (rowValueRaw === undefined) return false; // field missing → does not match
            if (operator === 'eq') {
              return normalize(rowValueRaw) === normalize(value);
            }
            if (operator === 'ne') {
              return normalize(rowValueRaw) !== normalize(value);
            }
            if (operator === 'in') {
              const vals = Array.isArray(value) ? value : [value];
              const normalizedVals = vals.map(normalize);
              return normalizedVals.includes(normalize(rowValueRaw));
            }
            if (operator === 'nin') {
              const vals = Array.isArray(value) ? value : [value];
              const normalizedVals = vals.map(normalize);
              return !normalizedVals.includes(normalize(rowValueRaw));
            }
            // Fallback: strict equality
            return rowValueRaw === value;
          });
        });
      }

      // Fallback: Some live datasets provide per-order-type columns instead of an 'order_type' dimension
      if ((filteredData.length === 0) && Array.isArray(visualization.dataQuery?.filters)) {
        const otFilter = visualization.dataQuery!.filters.find(f => String(f.field).toLowerCase() === 'order_type' && f.operator === 'eq');
        if (otFilter) {
          const otVal = String(otFilter.value).toLowerCase();
          const fieldMap: Record<string, string> = {
            'dine_in': 'dine_in_sales',
            'take_out': 'take_out_sales',
            'delivery': 'delivery_sales',
            'catering': 'catering_sales'
          };
          const altField = fieldMap[otVal];
          if (altField) {
            const sample = processedData[0] as any;
            if (sample && (altField in sample)) {
              // Synthesize rows by remapping the metric to the per-type field
              filteredData = processedData.map(row => {
                const cloned: any = { ...row };
                cloned[metricField] = Number((row as any)[altField]) || 0;
                return cloned;
              }).filter(row => Number((row as any)[metricField]) > 0);
              console.log(`📊 [SmartDataManager] Performance card '${visualization.id}' used per-type column fallback '${altField}' for '${otVal}', rows: ${filteredData.length}`);
            }
          }
        }
      }

      // Check if this metric is a calculated field (e.g., avg_order_value = total_sales / order_count)
      // If so, we need to sum the dependencies and recalculate, not sum the metric directly
      const metricFormula = cardConfig.formula as string | undefined;
      const isCalculatedMetric = !!metricFormula;

      // Parse formula dependencies if this is a calculated metric
      let formulaDeps: string[] = [];
      if (isCalculatedMetric && metricFormula) {
        const matches = metricFormula.match(/\{\{(\w+)\}\}/g) || [];
        formulaDeps = matches.map(m => m.replace(/\{\{|\}\}/g, ''));
        console.log(`📊 [CALC] Performance card '${visualization.id}' uses calculated metric: ${metricField} = ${metricFormula} (deps: ${formulaDeps.join(', ')})`);
      }

      // Group by location and aggregate (using filtered data)
      const grouped = new Map<string, {
        totals: Record<string, number>;
        data: AnalyticsDataPoint;
        count: number;
      }>();

      // Filter out rows that don't have the groupByField (e.g., parent rows in hierarchical tables)
      const validRows = filteredData.filter(record => {
        const key = record[groupByField];
        return key !== undefined && key !== null && key !== '';
      });

      validRows.forEach((record, index) => {
        const key = record[groupByField] as string;

        if (!grouped.has(key)) {
          grouped.set(key, {
            totals: {},
            data: { ...record },
            count: 0
          });
          // Initialize totals for dependencies (if calculated) or the metric itself
          if (isCalculatedMetric) {
            formulaDeps.forEach(dep => {
              grouped.get(key)!.totals[dep] = 0;
            });
          } else {
            grouped.get(key)!.totals[metricField] = 0;
          }
        }

        const existing = grouped.get(key)!;
        existing.count++;

        if (isCalculatedMetric) {
          // Sum the dependency fields, not the metric
          formulaDeps.forEach(dep => {
            existing.totals[dep] += Number(record[dep]) || 0;
          });
        } else {
          // Regular summing for non-calculated metrics
          existing.totals[metricField] += Number(record[metricField]) || 0;
        }
      });

      // Convert to array and calculate final metric values
      const aggregated = Array.from(grouped.values()).map(item => {
        const result = { ...item.data };

        if (isCalculatedMetric && metricFormula) {
          // Recalculate the metric using the formula with aggregated dependencies
          let expression = metricFormula;
          formulaDeps.forEach(dep => {
            const depValue = item.totals[dep] || 0;
            expression = expression.replace(new RegExp(`\\{\\{${dep}\\}\\}`, 'g'), String(depValue));
            // Also store the aggregated dependency values in the result
            result[dep] = depValue;
          });

          // Safely evaluate the expression
          try {
            const sanitized = expression.replace(/[^0-9+\-*/().  ]/g, '');
            const calculated = Function('"use strict"; return (' + sanitized + ')')();
            result[metricField] = isFinite(calculated) ? calculated : 0;
          } catch (e) {
            console.warn(`Failed to evaluate formula for performance card ${visualization.id}: ${expression}`, e);
            result[metricField] = 0;
          }
        } else {
          // Use the summed value for non-calculated metrics
          result[metricField] = item.totals[metricField];
        }

        return result;
      });

      aggregated.sort((a, b) => {
        const aVal = Number(a[metricField]) || 0;
        const bVal = Number(b[metricField]) || 0;
        return isLowest ? aVal - bVal : bVal - aVal;
      });

      // Return top result
      processedData = aggregated.slice(0, 1);
    }

    // Special processing for hierarchical tables
    if (visualization.type === 'table' && (visualization.config as any)?.hierarchical) {
      const tableConfig = visualization.config as any;
      const query = visualization.dataQuery;
      const hierarchyLevels = query?.hierarchyLevels;
      if (!hierarchyLevels || hierarchyLevels.length === 0) {
        throw new Error(`Hierarchical table '${visualization.id}' requires dataQuery.hierarchyLevels to be defined - no hardcoded fallbacks allowed`);
      }
      const includeSubtotals = query?.includeSubtotals !== false;
      const includeTotals = query?.includeTotals !== false;

      // Check if this visualization requires comparison fields (for variance highlighting)
      const includeComparisonFields = visualization.requiresHistoricalData === true;

      // PERFORMANCE: Check hierarchy cache first
      const hierarchyCacheKey = this.getHierarchyCacheKey(visualization.id, processedData, hierarchyLevels, includeComparisonFields);
      const cachedHierarchy = this.hierarchyCache.get(hierarchyCacheKey);

      if (cachedHierarchy && Date.now() - cachedHierarchy.timestamp < this.CACHE_TTL) {
        console.log(`⏱️ [PERF] Hierarchy CACHE HIT for ${visualization.id} - skipping rebuild of ${processedData.length} records`);
        processedData = cachedHierarchy.result;
      } else {
        // Building hierarchical structure
        const hierarchyStart = performance.now();

        // Pass columns to buildHierarchicalTable so it can handle calculated fields
        const columns = tableConfig?.columns as TableColumn[] | undefined;

        // Extract date filter for variance calculations
        const dateFilterId = tableConfig?.dateFilterId || 'global-date-range';
        const dateFilter = filters[dateFilterId];

        processedData = this.buildHierarchicalTable(
          processedData,
          hierarchyLevels,
          includeSubtotals,
          includeTotals,
          visualization.id,
          columns,
          dateFilter,
          includeComparisonFields
        );

        // Cache the result
        this.hierarchyCache.set(hierarchyCacheKey, {
          result: processedData,
          timestamp: Date.now()
        });

        const hierarchyEnd = performance.now();
        console.log(`⏱️ [PERF] Hierarchy built and CACHED for ${visualization.id}: ${(hierarchyEnd - hierarchyStart).toFixed(2)}ms`);
      }
      // Hierarchy building completed
    }

    // Special processing for calculated KPI cards with ranking (top/lowest location)
    if (visualization.type === 'kpi' && (visualization.config as any)?.mode === 'calculated' && (visualization.config as any)?.rankingType) {
      const kpiConfig = visualization.config as any;
      const metricField = kpiConfig.primaryMetric;
      const rankingType = kpiConfig.rankingType; // 'highest' or 'lowest'
      const locationDimension = kpiConfig.locationDimension || 'location';
      const isLowest = rankingType === 'lowest';

      console.log(`📊 [KPI Ranking] Processing calculated KPI '${visualization.id}' with rankingType: ${rankingType}, dimension: ${locationDimension}`);
      console.log(`📊 [KPI Ranking] Input data has ${processedData.length} records`);

      // Step 0: Filter to current period only (ranking must use same period as displayed value)
      // When requiresHistoricalData is true, processedData includes previous + year-ago periods.
      // The UI displays current period only, so we rank by current period to avoid mismatch.
      let rankingData = processedData;
      const dateFilterId = kpiConfig.dateFilterId || 'global-date-range';
      const dateFilter = filters[dateFilterId];
      if (dateFilter?.start && dateFilter?.end) {
        const resolveDate = (v: any): Date => {
          if (!v) return new Date();
          if (v instanceof Date) return v;
          if (typeof v === 'string') {
            if (DateConstantsService.isDateConstant(v)) return new Date(DateConstantsService.resolveDateConstant(v));
            return new Date(v);
          }
          return new Date(v);
        };
        const currentStart = resolveDate(dateFilter.start);
        const currentEnd = resolveDate(dateFilter.end);
        const currentStartStr = currentStart.toISOString().split('T')[0];
        const currentEndStr = currentEnd.toISOString().split('T')[0];
        rankingData = processedData.filter((record) => {
          const rawDate = record.date || record.dateFormatted || (record as any).timeKey || (record as any).time;
          if (!rawDate) return false;
          const dateStr = typeof rawDate === 'string'
            ? (rawDate.includes('T') ? rawDate.split('T')[0] : rawDate)
            : new Date(rawDate).toISOString().split('T')[0];
          return dateStr >= currentStartStr && dateStr <= currentEndStr;
        });
        console.log(`📊 [KPI Ranking] Filtered to current period: ${rankingData.length} records (${currentStartStr} to ${currentEndStr})`);
      }

      // Step 1: Group by location and aggregate metric across time (current period only)
      const locationTotals = new Map<string, number>();

      rankingData.forEach((record) => {
        const locationKey = record[locationDimension] as string;
        if (!locationKey) return; // Skip records without location

        const metricValue = Number(record[metricField]) || 0;

        if (!locationTotals.has(locationKey)) {
          locationTotals.set(locationKey, 0);
        }
        locationTotals.set(locationKey, locationTotals.get(locationKey)! + metricValue);
      });

      console.log(`📊 [KPI Ranking] Found ${locationTotals.size} unique locations:`, Array.from(locationTotals.entries()));

      // Step 2: Find the top or lowest location
      // Filter out locations with zero or negative values for lowest ranking
      const validLocations = Array.from(locationTotals.entries()).filter(([_, value]) => value > 0);

      const sortedLocations = validLocations.sort((a, b) => {
        return isLowest ? a[1] - b[1] : b[1] - a[1];
      });

      console.log(`📊 [KPI Ranking] Sorted locations (${rankingType}):`, sortedLocations.map(([loc, val]) => `${loc}: ${val}`));

      if (sortedLocations.length > 0) {
        const targetLocation = sortedLocations[0][0];
        const targetLocationTotal = sortedLocations[0][1];
        console.log(`📊 [KPI Ranking] ✅ Selected ${rankingType} location: "${targetLocation}" with total ${metricField}: ${targetLocationTotal}`);

        // Step 3: Filter processedData to only include records for the target location
        // AND add the location name to each record for frontend display
        processedData = processedData
          .filter(record => record[locationDimension] === targetLocation)
          .map(record => ({
            ...record,
            _selectedLocationName: targetLocation // Special field for frontend to display
          }));

        console.log(`📊 [KPI Ranking] Filtered to ${processedData.length} records for location: "${targetLocation}"`);
      } else {
        console.warn(`📊 [KPI Ranking] ⚠️ No valid locations found for KPI '${visualization.id}' (all locations have zero or negative values)`);
        processedData = [];
      }
    }

    // Special processing for calculated performance cards with ranking (top/lowest location/entity)
    if (visualization.type === 'performance-card' && (visualization.config as any)?.mode === 'calculated' && (visualization.config as any)?.rankingType) {
      const cardConfig = visualization.config as any;
      const metricField = cardConfig.metricField;
      const rankingType = cardConfig.rankingType; // 'highest' or 'lowest'
      const displayField = cardConfig.displayField || 'location'; // Default to 'location' if not specified
      const isLowest = rankingType === 'lowest';

      console.log(`📊 [PerformanceCard Ranking] Processing calculated performance card '${visualization.id}' with rankingType: ${rankingType}, metricField: ${metricField}, displayField: ${displayField}`);
      console.log(`📊 [PerformanceCard Ranking] Input data has ${processedData.length} records`);

      if (!metricField) {
        console.warn(`📊 [PerformanceCard Ranking] ⚠️ Performance card '${visualization.id}' has rankingType but no metricField specified`);
      } else {
        // Step 1: Determine if this is a ratio/formula metric (like avg_order_value = net_sales / order_count)
        // If so, we need to aggregate the components separately, not average the calculated values
        const hasFormula = cardConfig.formula && typeof cardConfig.formula === 'string';
        let formulaDependencies: string[] = [];

        if (hasFormula) {
          const matches = cardConfig.formula.match(/\{\{(\w+)\}\}/g) || [];
          formulaDependencies = matches.map(m => m.replace(/\{\{|\}\}/g, ''));
          console.log(`📊 [PerformanceCard Ranking] Formula detected: ${cardConfig.formula}`);
          console.log(`📊 [PerformanceCard Ranking] Dependencies: ${formulaDependencies.join(', ')}`);
        }

        // Step 2: Group by displayField and aggregate
        // For formula metrics, aggregate each dependency separately
        // For non-formula metrics, just sum the metric value
        const entityData = new Map<string, {
          components: Record<string, number>;
          directTotal: number;
          rowCount: number;
          record: any
        }>();

        // Filter out parent rows (hasChildren: true) when ranking by register or other leaf-level entities
        // Parent rows represent aggregated data and shouldn't be included in leaf-level rankings
        const dataToProcess = displayField === 'register'
          ? processedData.filter((record: any) => !record.hasChildren)
          : processedData;

        console.log(`📊 [PerformanceCard Ranking] Processing calculated performance card '${visualization.id}' with rankingType: ${rankingType}, metricField: ${metricField}, displayField: ${displayField}`);
        console.log(`📊 [PerformanceCard Ranking] Processing ${dataToProcess.length} records (filtered from ${processedData.length} total)`);

        dataToProcess.forEach((record) => {
          const entityKey = record[displayField] as string;
          if (!entityKey) return; // Skip records without the display field

          if (!entityData.has(entityKey)) {
            entityData.set(entityKey, {
              components: {},
              directTotal: 0,
              rowCount: 0,
              record: record
            });
          }

          const data = entityData.get(entityKey)!;
          data.rowCount += 1;

          if (hasFormula && formulaDependencies.length > 0) {
            // Aggregate each formula component separately (e.g., sum net_sales, sum order_count)
            formulaDependencies.forEach(dep => {
              if (!data.components[dep]) {
                data.components[dep] = 0;
              }
              data.components[dep] += Number(record[dep]) || 0;
            });
          } else {
            // No formula - just sum the metric field directly
            const metricValue = Number(record[metricField]) || 0;
            data.directTotal += metricValue;
          }
        });

        console.log(`📊 [PerformanceCard Ranking] Found ${entityData.size} unique entities`);

        // Step 3: Calculate final metric value for each entity
        const entityValues = Array.from(entityData.entries()).map(([entityKey, data]) => {
          let finalValue = 0;

          if (hasFormula && formulaDependencies.length > 0) {
            // Calculate the formula using aggregated components
            // Example: avg_order_value = total_net_sales / total_order_count (NOT avg of daily averages!)
            let expression = cardConfig.formula;
            formulaDependencies.forEach(dep => {
              const componentTotal = data.components[dep] || 0;
              expression = expression.replace(new RegExp(`\\{\\{${dep}\\}\\}`, 'g'), String(componentTotal));
            });

            try {
              const sanitized = expression.replace(/[^0-9+\-*/().  ]/g, '');
              finalValue = Function('"use strict"; return (' + sanitized + ')')();
              if (!isFinite(finalValue)) {
                finalValue = 0;
              }
              console.log(`📊 [PerformanceCard Ranking] Entity "${entityKey}": formula ${cardConfig.formula} → ${expression} = ${finalValue}`);
            } catch (e) {
              console.warn(`📊 [PerformanceCard Ranking] Failed to evaluate formula for entity "${entityKey}":`, expression, e);
              finalValue = 0;
            }
          } else {
            // No formula - use aggregated total (sum for most metrics, avg for avg metrics)
            const isAvgMetric = metricField.includes('avg') || metricField.includes('average');
            finalValue = isAvgMetric && data.rowCount > 0 ? data.directTotal / data.rowCount : data.directTotal;
          }

          return {
            entityKey,
            value: finalValue,
            components: data.components,
            record: data.record
          };
        });

        // Step 4: Find the top or lowest entity
        // Filter out entities with zero or negative values for lowest ranking
        const validEntities = entityValues.filter(({ value }) => value > 0);

        const sortedEntities = validEntities.sort((a, b) => {
          return isLowest ? a.value - b.value : b.value - a.value;
        });

        console.log(`📊 [PerformanceCard Ranking] Sorted entities (${rankingType}):`,
          sortedEntities.map(({ entityKey, value, components }) =>
            `${entityKey}: ${value}${hasFormula ? ` (${JSON.stringify(components)})` : ''}`
          )
        );

        if (sortedEntities.length > 0) {
          const targetEntity = sortedEntities[0];
          console.log(`📊 [PerformanceCard Ranking] ✅ Selected ${rankingType} entity: "${targetEntity.entityKey}" with ${metricField}: ${targetEntity.value}`);

          // Step 5: Return only the top/lowest performer with the calculated metric value
          processedData = [{
            ...targetEntity.record,
            [metricField]: targetEntity.value,
            [displayField]: targetEntity.entityKey,
            // Include the aggregated components for transparency/debugging
            ...targetEntity.components
          }];

          console.log(`📊 [PerformanceCard Ranking] Returning single record for entity: "${targetEntity.entityKey}"`);
        } else {
          console.warn(`📊 [PerformanceCard Ranking] ⚠️ No valid entities found for performance card '${visualization.id}' (all entities have zero or negative values)`);
          processedData = [];
        }
      }
    }

    // Special processing for formula-based KPIs (calculate derived metrics)
    if (visualization.type === 'kpi' && (visualization.config as any)?.mode === 'formula') {
      const kpiConfig = visualization.config as any;
      const formula = kpiConfig.formula;
      const metricField = kpiConfig.primaryMetric;

      if (!formula || typeof formula !== 'string') {
        console.warn(`📊 [KPI Formula] KPI '${visualization.id}' has mode=formula but no formula specified`);
      } else {
        console.log(`📊 [KPI Formula] Processing formula KPI '${visualization.id}': ${formula}`);

        // Parse formula to extract dependencies like {{dine_in_sales}}, {{net_sales}}
        const matches = formula.match(/\{\{(\w+)\}\}/g) || [];
        const dependencies = matches.map(m => m.replace(/\{\{|\}\}/g, ''));
        console.log(`📊 [KPI Formula] Dependencies: ${dependencies.join(', ')}`);

        // Calculate formula for each row in processedData
        processedData = processedData.map(row => {
          let expression = formula;

          // Replace each dependency with its actual value
          dependencies.forEach(dep => {
            const value = Number(row[dep]) || 0;
            expression = expression.replace(new RegExp(`\\{\\{${dep}\\}\\}`, 'g'), String(value));
          });

          // Safely evaluate the expression
          let calculatedValue = 0;
          try {
            // Remove any non-math characters for safety
            const sanitized = expression.replace(/[^0-9+\-*/().  ]/g, '');
            calculatedValue = Function('"use strict"; return (' + sanitized + ')')();

            // Handle NaN, Infinity, etc.
            if (!isFinite(calculatedValue)) {
              calculatedValue = 0;
            }
          } catch (e) {
            console.warn(`📊 [KPI Formula] Failed to evaluate formula for row:`, expression, e);
            calculatedValue = 0;
          }

          // Add the calculated value to the row
          return {
            ...row,
            [metricField]: calculatedValue
          };
        });

        console.log(`📊 [KPI Formula] Calculated ${metricField} for ${processedData.length} rows`);
      }
    }

    // Special processing for Mix Bar visualizations
    if (visualization.type === 'mix-bar') {
      const mixConfig = visualization.config as any;
      const metricField = mixConfig?.primaryMetric;
      const groupByField = mixConfig?.groupBy;
      const aggregation: 'sum' | 'avg' | 'min' | 'max' = mixConfig?.aggregation || 'sum';

      if (!metricField || !groupByField) {
        throw new Error(`Mix Bar '${visualization.id}' requires config.primaryMetric and config.groupBy`);
      }

      console.log(`📊 [Mix Bar] Processing ${visualization.id}: groupBy=${groupByField}, metric=${metricField}`);
      console.log(`📊 [Mix Bar] Raw data rows: ${processedData.length}`, processedData.slice(0, 5));

      // Group by category (e.g., zone, order_type, revenue_center)
      const grouped: Record<string, number[]> = {};
      processedData.forEach((row: any) => {
        const category = row[groupByField];
        if (category) {
          if (!grouped[category]) {
            grouped[category] = [];
          }
          const value = Number(row[metricField]) || 0;
          grouped[category].push(value);
        }
      });

      console.log(`📊 [Mix Bar] Grouped categories:`, Object.keys(grouped), grouped);

      // Aggregate values for each category
      const categoryValues: Record<string, number> = {};
      Object.entries(grouped).forEach(([category, values]) => {
        let result = 0;
        switch (aggregation) {
          case 'sum':
            result = values.reduce((s, v) => s + v, 0);
            break;
          case 'avg':
            result = values.length ? values.reduce((s, v) => s + v, 0) / values.length : 0;
            break;
          case 'min':
            result = values.length ? Math.min(...values) : 0;
            break;
          case 'max':
            result = values.length ? Math.max(...values) : 0;
            break;
        }
        categoryValues[category] = result;
      });

      console.log(`📊 [Mix Bar] Aggregated values:`, categoryValues);

      // Calculate total
      const total = Object.values(categoryValues).reduce((sum, val) => sum + val, 0);

      // Default color palette (color-blind friendly)
      const defaultColors = [
        '#3b82f6', // blue
        '#10b981', // green
        '#f59e0b', // orange
        '#ef4444', // red
        '#8b5cf6', // purple
        '#06b6d4', // cyan
        '#ec4899', // pink
        '#84cc16', // lime
        '#f97316', // orange-red
        '#14b8a6', // teal
      ];

      const colors = mixConfig?.colors || defaultColors;

      // Create segments and sort by percentage descending
      const segments = Object.entries(categoryValues)
        .map(([category, value]) => ({
          category,
          value,
          percentage: total > 0 ? (value / total) * 100 : 0,
        }))
        .sort((a, b) => b.percentage - a.percentage)
        // Assign colors AFTER sorting
        .map((segment, index) => ({
          ...segment,
          color: colors[index % colors.length]
        }));

      console.log(`📊 [Mix Bar] Created ${segments.length} segments, total: ${total}`, segments);

      // Return segments as processed data
      processedData = segments as any[];
    }

    // Special processing for KPI aggregations (sum/avg/etc.) using filtered dataset
    if (visualization.type === 'kpi') {
      const kpiConfig = visualization.config as any;
      const metricField = kpiConfig?.primaryMetric;
      const aggregation: 'sum' | 'avg' | 'min' | 'max' | 'count' = kpiConfig?.aggregation || 'sum';
      if (!metricField) {
        throw new Error(`KPI '${visualization.id}' requires config.primaryMetric`);
      }

      // Check if KPI has time-series grouping (groupBy includes 'time' or 'date')
      // If so, preserve the time-series data structure for sparkline rendering
      const dataQueryGroupBy = visualization.dataQuery?.groupBy || [];
      const hasTimeGrouping = dataQueryGroupBy.some(field => {
        const fieldName = String(field).valueOf();
        return fieldName === 'date' || fieldName === 'time';
      });

      // Check if data has time-series structure (multiple rows with date/time fields)
      const hasTimeSeriesStructure = processedData.length > 1 &&
        processedData.some((row: any) => row.date || row.dateFormatted || row.timeKey || row.time);

      // If KPI has time grouping AND data has time-series structure, preserve it for sparkline
      // The aggregated value will be calculated in TabbedAnalyticsDashboard from the time-series data
      if (hasTimeGrouping && hasTimeSeriesStructure) {
        // Preserve time-series data structure - don't aggregate
        // TabbedAnalyticsDashboard will calculate the aggregated value from this data
        // This allows sparkline to use the full time-series data
        console.log(`[KPI] Preserving time-series data for ${visualization.id}: ${processedData.length} data points`);
      } else {
        // Check if this KPI has a formula that should be calculated from aggregated components
        // Example: avg_ticket = net_sales / order_count
        // We should NOT average the daily avg_ticket values, but sum net_sales and sum order_count, then divide
        const hasFormula = kpiConfig.formula && typeof kpiConfig.formula === 'string';

        if (hasFormula && (aggregation === 'avg' || metricField.includes('avg') || metricField.includes('average'))) {
          console.log(`📊 [KPI Formula Aggregation] KPI '${visualization.id}' has formula with avg aggregation - calculating from components`);

          // Parse formula to get dependencies
          const matches = kpiConfig.formula.match(/\{\{(\w+)\}\}/g) || [];
          const dependencies = matches.map(m => m.replace(/\{\{|\}\}/g, ''));
          console.log(`📊 [KPI Formula Aggregation] Dependencies: ${dependencies.join(', ')}`);

          if (dependencies.length > 0) {
            // Aggregate each dependency separately
            const componentTotals: Record<string, number> = {};
            dependencies.forEach(dep => {
              componentTotals[dep] = processedData
                .map(row => Number((row as any)[dep]) || 0)
                .reduce((sum, val) => sum + val, 0);
            });

            console.log(`📊 [KPI Formula Aggregation] Component totals:`, componentTotals);

            // Calculate final value from aggregated components
            let expression = kpiConfig.formula;
            dependencies.forEach(dep => {
              expression = expression.replace(new RegExp(`\\{\\{${dep}\\}\\}`, 'g'), String(componentTotals[dep]));
            });

            try {
              const sanitized = expression.replace(/[^0-9+\-*/().  ]/g, '');
              const result = Function('"use strict"; return (' + sanitized + ')')();
              const finalValue = isFinite(result) ? result : 0;

              console.log(`📊 [KPI Formula Aggregation] Final calculation: ${kpiConfig.formula} → ${expression} = ${finalValue}`);
              processedData = [{
                [metricField]: finalValue,
                ...componentTotals // Include components for transparency
              } as any];
            } catch (e) {
              console.warn(`📊 [KPI Formula Aggregation] Failed to evaluate formula:`, expression, e);
              processedData = [{ [metricField]: 0 } as any];
            }
          } else {
            // No dependencies found, fall back to standard aggregation
            const values = processedData
              .map(row => Number((row as any)[metricField]) || 0)
              .filter(v => !isNaN(v));
            const result = values.length ? values.reduce((s, v) => s + v, 0) / values.length : 0;
            processedData = [{ [metricField]: result } as any];
          }
        } else {
          // Standard aggregation for non-formula KPIs or non-avg aggregations
          const values = processedData
            .map(row => Number((row as any)[metricField]) || 0)
            .filter(v => !isNaN(v));
          let result = 0;
          switch (aggregation) {
            case 'sum':
              result = values.reduce((s, v) => s + v, 0);
              break;
            case 'avg':
              result = values.length ? values.reduce((s, v) => s + v, 0) / values.length : 0;
              break;
            case 'min':
              result = values.length ? Math.min(...values) : 0;
              break;
            case 'max':
              result = values.length ? Math.max(...values) : 0;
              break;
            case 'count':
              result = values.length;
              break;
          }
          processedData = [{ [metricField]: result } as any];
        }
      }
    }

    const processEnd = performance.now();
    console.log(`⏱️ [PERF] processDataSetData complete for ${visualization.id}: ${rawData.length} → ${processedData.length} records in ${(processEnd - processStart).toFixed(2)}ms`);

    return processedData;
  }

  /**
   * Dynamically identify numeric fields from data (generic approach)
   */
  private getNumericFields(data: AnalyticsDataPoint[]): string[] {
    if (!data.length) return [];

    const numericFields: string[] = [];
    const sampleRow = data[0];

    Object.entries(sampleRow).forEach(([field, value]) => {
      // Skip framework-specific metadata fields 
      if (['id', 'date', 'dateFormatted', 'timeKey', 'level', 'parentId', 'hasChildren', 'expanded', '_displayValue'].includes(field)) {
        return;
      }

      // Include field if it contains numeric data
      if (typeof value === 'number') {
        numericFields.push(field);
      } else if (typeof value === 'string') {
        const numValue = parseFloat(value);
        if (!isNaN(numValue) && isFinite(numValue)) {
          numericFields.push(field);
        }
      }
    });

    return numericFields;
  }

  /**
   * Pivoted chart rows are sparse: a location may be missing from early time buckets.
   * Union numeric keys from ALL rows so totals and top/bottom match KPI / table (full-period sums).
   */
  private getNumericSeriesFieldsFromChartRows(
    chartData: AnalyticsDataPoint[],
    frameworkFields: string[]
  ): string[] {
    const seen = new Set<string>();
    for (const row of chartData) {
      for (const field of Object.keys(row)) {
        if (frameworkFields.includes(field)) continue;
        const v = row[field];
        if (typeof v === 'number' && Number.isFinite(v)) {
          seen.add(field);
        }
      }
    }
    return Array.from(seen);
  }

  /**
   * Limit the number of series in chart data to prevent overwhelming visualizations
   * When splitLocations is enabled, keeps all location series for toggle functionality
   * When showTopAndBottom is enabled, shows only top N and bottom N performers together
   * When disabled, shows only top K performers
   */
  private limitChartSeries(
    chartData: AnalyticsDataPoint[],
    seriesDimension: string,
    maxSeries: number,
    splitLocations: boolean = false,
    showEnterprise: boolean = false,
    metricField: string = 'value',
    showTopAndBottom: boolean = false,
    topBottomCount: number = 1
  ): AnalyticsDataPoint[] {
    if (!chartData.length || maxSeries <= 0) return chartData;

    // Identify series columns — scan all rows, not row[0] (sparse pivot)
    const frameworkFields = ['date', 'dateFormatted', 'timeKey', 'recordCount'];
    const seriesFields = this.getNumericSeriesFieldsFromChartRows(chartData, frameworkFields);

    // If we have 1 or fewer series, return as-is (just add enterprise if needed)
    if (seriesFields.length <= 1) {
      if (showEnterprise && seriesFields.length > 0) {
        return this.addEnterpriseLineToChart(chartData, seriesFields, frameworkFields);
      }
      return chartData;
    }

    let seriesToKeep: string[] = [];

    if (showTopAndBottom) {
      // Show both top N and bottom N performers together (no toggles)
      const seriesTotals: Record<string, number> = {};
      seriesFields.forEach(field => {
        seriesTotals[field] = chartData.reduce((sum, row) => {
          return sum + (Number(row[field]) || 0);
        }, 0);
      });

      // Match calculated KPI ranking: only locations with total > 0 participate
      const positiveEntries = Object.entries(seriesTotals).filter(([, v]) => v > 0);
      const sortedDesc = [...positiveEntries].sort(([, a], [, b]) => b - a);
      const sortedAsc = [...positiveEntries].sort(([, a], [, b]) => a - b);

      const topN = Math.min(topBottomCount, sortedDesc.length);
      const bottomN = Math.min(topBottomCount, sortedAsc.length);
      const topSeries = sortedDesc.slice(0, topN).map(([name]) => name);
      const bottomSeries = sortedAsc.slice(0, bottomN).map(([name]) => name);

      // Combine (use Set to avoid duplicates if total series < 2*N)
      seriesToKeep = Array.from(new Set([...topSeries, ...bottomSeries]));

      console.log(`📊 [Top & Bottom Mode] Showing top ${topN} and bottom ${bottomN} performers:`, seriesToKeep);
    } else if (splitLocations) {
      // When splitLocations is enabled, keep ALL location series
      // The toggle filtering will happen in the chart component
      seriesToKeep = seriesFields;
    } else {
      // Top K only — positive totals only (same as calculated KPI highest ranking)
      const seriesTotals: Record<string, number> = {};
      seriesFields.forEach(field => {
        seriesTotals[field] = chartData.reduce((sum, row) => {
          return sum + (Number(row[field]) || 0);
        }, 0);
      });

      const positiveSorted = Object.entries(seriesTotals)
        .filter(([, v]) => v > 0)
        .sort(([, a], [, b]) => b - a);

      const topK = Math.min(maxSeries, positiveSorted.length);
      seriesToKeep = positiveSorted.slice(0, topK).map(([name]) => name);
    }

    // Filter chart data to only include framework fields + selected series
    const fieldsToKeep = [...frameworkFields, ...seriesToKeep];

    let filteredData = chartData.map(row => {
      const filteredRow: any = {};
      fieldsToKeep.forEach(field => {
        if (row.hasOwnProperty(field)) {
          filteredRow[field] = row[field];
        }
      });
      return filteredRow;
    });

    // Add enterprise line if requested
    if (showEnterprise) {
      filteredData = this.addEnterpriseLineToChart(filteredData, seriesToKeep, frameworkFields);
    }

    return filteredData;
  }

  /**
   * Add enterprise aggregate line to chart data
   */
  private addEnterpriseLineToChart(
    chartData: AnalyticsDataPoint[],
    seriesFields: string[],
    frameworkFields: string[]
  ): AnalyticsDataPoint[] {
    return chartData.map(row => {
      // Calculate enterprise total by summing all location values for this time point
      const enterpriseTotal = seriesFields.reduce((sum, field) => {
        return sum + (Number(row[field]) || 0);
      }, 0);

      return {
        ...row,
        'ENTERPRISE': enterpriseTotal
      };
    });
  }

  /**
   * Evaluate a table column formula ({{dep}} placeholders) against a summable aggregate map.
   * Used for current period and for WoW/MoM/YoY comparison aggregates on calculated columns.
   */
  private evaluateCalculatedFormulaFromAggregates(
    formula: { expression: string; deps: string[] },
    aggregates: Record<string, number>
  ): number {
    let expression = formula.expression;
    formula.deps.forEach(dep => {
      const depValue = aggregates[dep] || 0;
      expression = expression.replace(new RegExp(`\\{\\{${dep}\\}\\}`, 'g'), String(depValue));
    });
    try {
      const sanitized = expression.replace(/[^0-9+\-*/().  ]/g, '');
      const result = Function('"use strict"; return (' + sanitized + ')')();
      return isFinite(result) ? result : 0;
    } catch (e) {
      console.warn(`Failed to evaluate calculated formula: ${expression}`, e);
      return 0;
    }
  }

  /**
   * Build hierarchical table structure from flat data
   * 
   * Supports calculated fields via column.mode = 'calculated' with formula like "{{total_sales}} / {{order_count}}"
   * These fields are NOT summed during aggregation - instead they're recalculated from the formula
   * using the aggregated values of their dependency fields.
   */
  private buildHierarchicalTable(
    flatData: AnalyticsDataPoint[],
    hierarchyLevels: string[],
    includeSubtotals: boolean,
    includeTotals: boolean,
    tableId?: string,
    columns?: TableColumn[],
    dateFilter?: { start: any; end: any },
    includeComparisonFields: boolean = false
  ): AnalyticsDataPoint[] {
    const hierarchyStart = performance.now();
    console.log(`⏱️ [PERF] Starting hierarchical table build for ${tableId}: ${flatData.length} records, ${hierarchyLevels.length} levels, includeComparisonFields: ${includeComparisonFields}`);

    if (!flatData.length) return flatData;

    // Identify numeric fields dynamically from the data
    const numericFields = this.getNumericFields(flatData);

    // Parse columns to find calculated fields (mode: 'calculated' with formula)
    // These fields should NOT be summed - they'll be recalculated after aggregation
    const calculatedFieldFormulas = new Map<string, { expression: string; deps: string[] }>();

    if (columns) {
      columns.forEach(col => {
        if (col.mode === 'calculated' && col.formula && typeof col.formula === 'string') {
          // Parse formula like "{{total_sales}} / {{order_count}}" to extract dependencies
          const matches = col.formula.match(/\{\{(\w+)\}\}/g) || [];
          const deps = matches.map(m => m.replace(/\{\{|\}\}/g, ''));
          calculatedFieldFormulas.set(col.field, {
            expression: col.formula,
            deps
          });
          console.log(`📊 [CALC] Found calculated field: ${col.field} = ${col.formula} (deps: ${deps.join(', ')})`);
        }
      });
    }

    // Separate summable fields from calculated fields
    const calculatedFieldNames = Array.from(calculatedFieldFormulas.keys());
    const summableFields = numericFields.filter(f => !calculatedFieldNames.includes(f));

    // VARIANCE SUPPORT: Separate data by time periods if comparison fields are requested
    let currentPeriodData = flatData;
    let wowPeriodData: AnalyticsDataPoint[] = [];
    let momPeriodData: AnalyticsDataPoint[] = [];
    let yoyPeriodData: AnalyticsDataPoint[] = [];

    if (includeComparisonFields && dateFilter?.start && dateFilter?.end) {
      // Parse date filter
      const currentStart = DateConstantsService.isDateConstant(dateFilter.start)
        ? new Date(DateConstantsService.resolveDateConstant(dateFilter.start))
        : new Date(dateFilter.start);
      const currentEnd = DateConstantsService.isDateConstant(dateFilter.end)
        ? new Date(DateConstantsService.resolveDateConstant(dateFilter.end))
        : new Date(dateFilter.end);

      // Calculate comparison period dates
      const wowStart = new Date(currentStart);
      wowStart.setDate(wowStart.getDate() - 7);
      const wowEnd = new Date(currentEnd);
      wowEnd.setDate(wowEnd.getDate() - 7);

      // MoM: Calendar month alignment (same days of the month, previous month)
      // Example: Nov 1-10 → Oct 1-10
      const momStart = new Date(currentStart);
      momStart.setMonth(momStart.getMonth() - 1);

      const momEnd = new Date(currentEnd);
      momEnd.setMonth(momEnd.getMonth() - 1);

      // Validate that MoM dates are valid (check for rollover issues like Jan 31 → Feb)
      // If dates rolled over, the comparison period would be invalid
      const momStartDay = currentStart.getDate();
      const momEndDay = currentEnd.getDate();
      const momStartValid = momStart.getDate() === momStartDay &&
        momStart.getMonth() === (currentStart.getMonth() - 1 + 12) % 12;
      const momEndValid = momEnd.getDate() === momEndDay &&
        momEnd.getMonth() === (currentEnd.getMonth() - 1 + 12) % 12;

      const yoyStart = new Date(currentStart);
      yoyStart.setFullYear(yoyStart.getFullYear() - 1);
      const yoyEnd = new Date(currentEnd);
      yoyEnd.setFullYear(yoyEnd.getFullYear() - 1);

      // Convert to date strings for comparison
      const currentStartStr = currentStart.toISOString().split('T')[0];
      const currentEndStr = currentEnd.toISOString().split('T')[0];
      const wowStartStr = wowStart.toISOString().split('T')[0];
      const wowEndStr = wowEnd.toISOString().split('T')[0];
      const momStartStr = momStart.toISOString().split('T')[0];
      const momEndStr = momEnd.toISOString().split('T')[0];
      const yoyStartStr = yoyStart.toISOString().split('T')[0];
      const yoyEndStr = yoyEnd.toISOString().split('T')[0];

      // Helper to extract date string from row
      const getRowDateStr = (row: any): string | null => {
        const dateValue = row.date || row.dateFormatted || row.timeKey || row.time;
        if (!dateValue) return null;
        try {
          return typeof dateValue === 'string'
            ? (dateValue.includes('T') ? dateValue.split('T')[0] : dateValue)
            : new Date(dateValue).toISOString().split('T')[0];
        } catch {
          return null;
        }
      };

      // Separate data into periods
      currentPeriodData = flatData.filter(row => {
        const dateStr = getRowDateStr(row);
        return dateStr && dateStr >= currentStartStr && dateStr <= currentEndStr;
      });

      wowPeriodData = flatData.filter(row => {
        const dateStr = getRowDateStr(row);
        return dateStr && dateStr >= wowStartStr && dateStr <= wowEndStr;
      });

      // MoM: Only filter if dates are valid (not rolled over)
      if (momStartValid && momEndValid) {
        const momStartStr = momStart.toISOString().split('T')[0];
        const momEndStr = momEnd.toISOString().split('T')[0];

        momPeriodData = flatData.filter(row => {
          const dateStr = getRowDateStr(row);
          return dateStr && dateStr >= momStartStr && dateStr <= momEndStr;
        });
      } else {
        // Invalid MoM dates (e.g., Jan 31 → Feb doesn't have 31st)
        momPeriodData = [];
      }

      yoyPeriodData = flatData.filter(row => {
        const dateStr = getRowDateStr(row);
        return dateStr && dateStr >= yoyStartStr && dateStr <= yoyEndStr;
      });

      console.log(`📊 [Hierarchy+Variance] Separated data into periods:`, {
        current: currentPeriodData.length,
        wow: wowPeriodData.length,
        mom: momPeriodData.length,
        yoy: yoyPeriodData.length,
        total: flatData.length
      });
    }

    // Define hierarchy node interface with comparison aggregates
    interface HierarchyNode {
      level: number;
      levelName: string;
      value: string;
      pathKey: string;
      data: AnalyticsDataPoint;
      aggregates: Record<string, number>;
      aggregatesWow: Record<string, number>;  // WoW comparison values
      aggregatesMom: Record<string, number>;  // MoM comparison values
      aggregatesYoy: Record<string, number>;  // YoY comparison values
      children: Map<string, HierarchyNode>;
      recordCount: number;
    }

    // Build hierarchical structure
    const hierarchy = new Map<string, HierarchyNode>();

    // Helper function to aggregate data into hierarchy
    const aggregateIntoHierarchy = (
      dataToAggregate: AnalyticsDataPoint[],
      targetMap: Map<string, HierarchyNode>,
      aggregateField: 'aggregates' | 'aggregatesWow' | 'aggregatesMom' | 'aggregatesYoy'
    ) => {
      dataToAggregate.forEach((record) => {
        let currentLevel = targetMap;
        let pathKey = '';

        hierarchyLevels.forEach((level, index) => {
          const value = record[level] as string || 'Unknown';
          pathKey += (pathKey ? '|' : '') + value;

          if (!currentLevel.has(value)) {
            currentLevel.set(value, {
              level: index,
              levelName: level,
              value: value,
              pathKey: pathKey,
              data: { ...record, [level]: value },
              aggregates: {},
              aggregatesWow: {},
              aggregatesMom: {},
              aggregatesYoy: {},
              children: new Map(),
              recordCount: 0
            });

            // Initialize aggregates for all periods
            summableFields.forEach(field => {
              currentLevel.get(value)!.aggregates[field] = 0;
              currentLevel.get(value)!.aggregatesWow[field] = 0;
              currentLevel.get(value)!.aggregatesMom[field] = 0;
              currentLevel.get(value)!.aggregatesYoy[field] = 0;
            });
          }

          const node = currentLevel.get(value)!;
          if (aggregateField === 'aggregates') {
            node.recordCount++;
          }

          // Aggregate summable numeric values into the specified aggregate field
          summableFields.forEach(field => {
            const val = Number(record[field]) || 0;
            node[aggregateField][field] += val;
          });

          currentLevel = node.children;
        });
      });
    };

    // Aggregate current period data (main aggregates)
    aggregateIntoHierarchy(currentPeriodData, hierarchy, 'aggregates');

    // Aggregate comparison periods if enabled
    if (includeComparisonFields) {
      aggregateIntoHierarchy(wowPeriodData, hierarchy, 'aggregatesWow');
      aggregateIntoHierarchy(momPeriodData, hierarchy, 'aggregatesMom');
      aggregateIntoHierarchy(yoyPeriodData, hierarchy, 'aggregatesYoy');
    }

    // Hierarchy structure built successfully (aggregated via aggregateIntoHierarchy calls above)

    // Convert hierarchy to flat list for table display
    let result: AnalyticsDataPoint[] = [];

    const addNode = (node: HierarchyNode, depth: number = 0, parentId?: string) => {

      // Create display record with unique ID for this hierarchy level
      const displayRecord: AnalyticsDataPoint = {
        ...node.data,
        id: `hierarchy-${node.pathKey}`, // Unique ID for this hierarchy level
        level: depth, // AnalyticsTable expects 'level'
        hasChildren: node.children.size > 0, // AnalyticsTable expects 'hasChildren'
        parentId: parentId, // AnalyticsTable expects 'parentId'
        expanded: false, // PERFORMANCE: Start collapsed for faster rendering (was: depth <= 2)
        _hierarchyKey: node.pathKey,
        _isSubtotal: depth < hierarchyLevels.length - 1,
        _recordCount: node.recordCount
      };

      // Use aggregated values for summable fields
      summableFields.forEach(field => {
        displayRecord[field] = node.aggregates[field];

        // Add comparison fields if variance calculation is enabled
        if (includeComparisonFields) {
          displayRecord[`${field}_wow`] = node.aggregatesWow[field] || 0;
          displayRecord[`${field}_mom`] = node.aggregatesMom[field] || 0;
          displayRecord[`${field}_yoy`] = node.aggregatesYoy[field] || 0;
        }
      });

      // Recalculate calculated fields using their formulas (current + comparison periods for variance)
      calculatedFieldFormulas.forEach((formula, field) => {
        displayRecord[field] = this.evaluateCalculatedFormulaFromAggregates(formula, node.aggregates);
        if (includeComparisonFields) {
          displayRecord[`${field}_wow`] = this.evaluateCalculatedFormulaFromAggregates(formula, node.aggregatesWow);
          displayRecord[`${field}_mom`] = this.evaluateCalculatedFormulaFromAggregates(formula, node.aggregatesMom);
          displayRecord[`${field}_yoy`] = this.evaluateCalculatedFormulaFromAggregates(formula, node.aggregatesYoy);
        }
      });

      // Set the current level's field to the current node value
      const currentLevel = hierarchyLevels[depth];
      displayRecord[currentLevel] = node.value;

      // Set a special display field that contains the appropriate value for this level
      displayRecord._displayValue = node.value;
      displayRecord._displayField = currentLevel;

      result.push(displayRecord);

      // Add children if includeSubtotals or if this is the last level
      const shouldAddChildren = includeSubtotals || depth === hierarchyLevels.length - 1;

      if (shouldAddChildren) {
        const sortedChildren = Array.from(node.children.values())
          .sort((a, b) => a.value.localeCompare(b.value));

        sortedChildren.forEach(child => {
          addNode(child, depth + 1, displayRecord.id); // Pass parent ID
        });
      }
    };

    // Add all top-level nodes
    const topLevelNodes = Array.from(hierarchy.values())
      .sort((a, b) => a.value.localeCompare(b.value));

    topLevelNodes.forEach(node => {
      addNode(node, 0); // Top level has no parent
    });

    // Filter out leaf nodes (registers) with zero values in the selected date range
    // Historical data should only be used for variance calculations, not for displaying rows
    if (hierarchyLevels.length > 0) {
      const leafLevel = hierarchyLevels.length - 1;
      const beforeFilterCount = result.length;

      result = result.filter(row => {
        // Keep all non-leaf rows (parents, subtotals, totals)
        if (row.level < leafLevel || row.level === -1) return true;

        // For leaf rows, check if they have any non-zero value in summable fields
        // If all summable fields are zero, it's "dead data" and should be filtered out
        const hasNonZeroValue = summableFields.some(field => {
          const value = Number(row[field]) || 0;
          return value !== 0;
        });

        return hasNonZeroValue;
      });

      if (beforeFilterCount !== result.length) {
        console.log(`📊 [Hierarchy] Filtered out ${beforeFilterCount - result.length} zero-value leaf nodes: ${result.length} rows remaining`);
      }
    }

    // Add grand total if requested
    if (includeTotals && topLevelNodes.length > 1) {
      const primaryDisplayField = hierarchyLevels[hierarchyLevels.length - 1];
      const grandTotal: AnalyticsDataPoint = {
        id: 'hierarchy-grand-total',
        [primaryDisplayField]: 'GRAND TOTAL',
        level: -1, // Special level for grand total
        hasChildren: false,
        expanded: false,
        _hierarchyKey: 'grand-total',
        _isSubtotal: false,
        _recordCount: flatData.length
      };

      // Calculate grand totals for summable fields only
      const grandTotalAggregates: Record<string, number> = {};
      const grandTotalAggregatesWow: Record<string, number> = {};
      const grandTotalAggregatesMom: Record<string, number> = {};
      const grandTotalAggregatesYoy: Record<string, number> = {};
      summableFields.forEach(field => {
        // Current period totals
        grandTotalAggregates[field] = currentPeriodData.reduce((sum, record) => {
          return sum + (Number(record[field]) || 0);
        }, 0);
        grandTotal[field] = grandTotalAggregates[field];

        // Comparison period totals
        if (includeComparisonFields) {
          grandTotalAggregatesWow[field] = wowPeriodData.reduce((sum, record) => {
            return sum + (Number(record[field]) || 0);
          }, 0);
          grandTotalAggregatesMom[field] = momPeriodData.reduce((sum, record) => {
            return sum + (Number(record[field]) || 0);
          }, 0);
          grandTotalAggregatesYoy[field] = yoyPeriodData.reduce((sum, record) => {
            return sum + (Number(record[field]) || 0);
          }, 0);
          grandTotal[`${field}_wow`] = grandTotalAggregatesWow[field];
          grandTotal[`${field}_mom`] = grandTotalAggregatesMom[field];
          grandTotal[`${field}_yoy`] = grandTotalAggregatesYoy[field];
        }
      });

      // Recalculate calculated fields for grand total (current + comparison for variance)
      calculatedFieldFormulas.forEach((formula, field) => {
        grandTotal[field] = this.evaluateCalculatedFormulaFromAggregates(formula, grandTotalAggregates);
        if (includeComparisonFields) {
          grandTotal[`${field}_wow`] = this.evaluateCalculatedFormulaFromAggregates(formula, grandTotalAggregatesWow);
          grandTotal[`${field}_mom`] = this.evaluateCalculatedFormulaFromAggregates(formula, grandTotalAggregatesMom);
          grandTotal[`${field}_yoy`] = this.evaluateCalculatedFormulaFromAggregates(formula, grandTotalAggregatesYoy);
        }
      });

      result.unshift(grandTotal);
    }

    const hierarchyEnd = performance.now();
    console.log(`⏱️ [PERF] Hierarchical table complete for ${tableId}: ${flatData.length} → ${result.length} records in ${(hierarchyEnd - hierarchyStart).toFixed(2)}ms`);

    return result;
  }

  /**
   * Legacy data loading with enhanced processing (EXISTING WITH IMPROVEMENTS)
   */
  private async loadVisualizationDataLegacy(
    visualization: AnalyticsVisualization,
    filters: Record<string, any>,
    forceRefresh = false
  ): Promise<AnalyticsResponse> {

    // Check cache first (existing logic)
    const cacheKey = this.getCacheKey(visualization, filters);
    if (!forceRefresh) {
      const cached = this.cache.get(cacheKey);
      if (cached && this.isCacheValid(cached.timestamp)) {
        return cached.data;
      }
    }

    let response: AnalyticsResponse;
    let chartData: AnalyticsDataPoint[] = [];

    // Determine data source
    if (!visualization.dataQuery) {
      // Use example response or placeholder
      response = {
        configId: visualization.dataQuery?.configId || 'example',
        data: visualization.exampleResponse || [],
        metadata: {
          totalRecords: visualization.exampleResponse?.length || 0,
          processingTime: 0,
          cacheHit: false,
          dataSource: 'example',
          lastRefreshed: new Date()
        }
      };
    } else {
      // Load from backend service
      const dateRange = this.extractDateRange(filters);
      const timeRange = this.extractTimeRange(filters);
      const query: AnalyticsDataQuery = {
        configId: visualization.dataQuery.configId,
        metrics: visualization.dataQuery.metrics || [],
        dimensions: visualization.dataQuery.dimensions,
        groupBy: [],
        filters: this.convertFiltersToQueryFilters(filters),
        dateRange: dateRange,
        timeRange: timeRange,
        limit: 1000
      };

      // Set visualization context for better endpoint selection
      if (this.activeService instanceof BackendAnalyticsService) {
        const hasCustomQuery = !!(visualization.dataQuery && visualization.dataQuery.metrics && visualization.dataQuery.metrics.length > 0);
        this.activeService.setVisualizationContext({
          id: visualization.id,
          hasCustomQuery
        });

      }

      response = await this.activeService.processAnalytics(query);
      chartData = response.data || [];

      // Clear visualization context after request
      if (this.activeService instanceof BackendAnalyticsService) {
        this.activeService.setVisualizationContext(undefined);
      }
    }

    // Apply calculated processing for charts (enhanced existing logic)
    if (visualization.type === 'chart' && (visualization.config as any)?.mode === 'calculated') {
      const chartConfig = visualization.config as any;

      if (chartConfig.sourceVisualizationId && chartConfig.dateFilterId) {
        // 🔧 FIX: Apply client-side filtering to calculated charts
        // Use DataSet framework to get properly filtered source data
        if (this.dataSetContext && this.dataSetManager) {

          try {
            // Get filtered data from DataSet framework for the source table
            const filteredSourceData = await this.dataSetManager.getVisualizationData(
              chartConfig.sourceVisualizationId,
              { dataSetId: this.dataSetContext?.dashboard?.id, scope: 'dashboard' },
              filters,
              this.currentTabId,
              this.dataSetContext?.dashboard?.id
            );

            chartData = filteredSourceData;

          } catch (error) {
            console.warn(`⚠️ [SmartDataManager] Failed to get filtered source data, using original:`, error);
            // Fall back to original data if filtering fails
          }
        }

        // Get source data (now potentially filtered)
        const sourceData = chartData;

        if (chartConfig.dateFilterId && filters[chartConfig.dateFilterId]) {
          const dateRange = filters[chartConfig.dateFilterId];
          if (dateRange?.start && dateRange?.end) {
            const metrics = chartConfig.metrics || ['total_sales'];
            const aggregation = chartConfig.aggregation || 'sum';
            const additionalGroupBy = chartConfig.groupBy || [];
            const timeGroupingRules = chartConfig.timeAxisRules;

            const timeGroupedResult = TimeGroupingService.processChartDataWithDimensions(
              sourceData,
              new Date(dateRange.start),
              new Date(dateRange.end),
              metrics,
              aggregation,
              additionalGroupBy.length > 0 ? additionalGroupBy : undefined,
              timeGroupingRules
            );

            chartData = timeGroupedResult.data;
            response.data = chartData;
            response.metadata = {
              ...response.metadata,
              totalRecords: chartData.length,
              processingTime: response.metadata?.processingTime || 0,
              cacheHit: false,
              dataSource: 'calculated-filtered',
              lastRefreshed: new Date()
            };

          }
        }
      }
    }

    // Cache the result
    this.cache.set(cacheKey, { data: response, timestamp: Date.now() });

    // Data processing completed

    return response;
  }

  /**
   * Helper methods for legacy compatibility
   */
  private getCacheKey(visualization: AnalyticsVisualization, filters: Record<string, any>): string {
    const filterKey = Object.entries(filters)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}:${JSON.stringify(v)}`)
      .join('|');
    return `${visualization.id}::${filterKey}`;
  }

  private isCacheValid(timestamp: number): boolean {
    return Date.now() - timestamp < this.CACHE_TTL;
  }

  private convertFiltersToRequest(filters: Record<string, any>): Array<{ filterId: string; value: any }> {
    return Object.entries(filters).map(([filterId, value]) => ({
      filterId,
      value
    }));
  }

  private convertFiltersToQueryFilters(filters: Record<string, any>): QueryFilter[] {
    return Object.entries(filters).map(([field, value]) => ({
      field,
      operator: 'eq' as const,
      value
    }));
  }

  private extractDateRange(filters: Record<string, any>): { start: Date | string; end: Date | string } | undefined {
    const dateEntry = Object.entries(filters).find(([k]) => k.includes('date'));
    if (dateEntry) {
      const [, value] = dateEntry;
      if (value && typeof value === 'object' && 'start' in value && 'end' in value) {
        return { start: value.start, end: value.end };
      }
    }
    return undefined;
  }

  private extractTimeRange(filters: Record<string, any>): { start: string; end: string } | undefined {
    const dateEntry = Object.entries(filters).find(([k]) => k.includes('date'));
    if (dateEntry) {
      const [, value] = dateEntry;
      if (value && typeof value === 'object' && 'startTime' in value && 'endTime' in value) {
        return { start: value.startTime, end: value.endTime };
      }
    }
    return undefined;
  }

  /**
   * Clear all caches - enhanced to include DataSetManager (NEW)
   */
  clearCache(): void {
    this.cache.clear();
    this.fullDataCache.clear();
    this.lastServerQueries.clear();
    this.dataSetManager.clearCache();
    this.hierarchyCache.clear(); // PERFORMANCE: Clear hierarchy cache too
  }

  /**
   * Get comprehensive cache statistics (NEW)
   */
  getCacheStats(): {
    legacyCache: number;
    dataSetCache: { totalItems: number; totalSize: number; datasets: string[] };
    dataSetFrameworkEnabled: boolean;
    hierarchyCache: number;
  } {
    return {
      legacyCache: this.cache.size,
      dataSetCache: this.dataSetManager.getCacheStats(),
      dataSetFrameworkEnabled: !!this.dataSetContext,
      hierarchyCache: this.hierarchyCache.size
    };
  }

  /**
   * Generate cache key for hierarchy results
   */
  private getHierarchyCacheKey(
    visualizationId: string,
    data: AnalyticsDataPoint[],
    hierarchyLevels: string[],
    includeComparisonFields: boolean = false
  ): string {
    // Use data length and first/last row as fingerprint
    const dataFingerprint = data.length > 0
      ? `${data.length}-${data[0]?.date}-${data[data.length - 1]?.date}`
      : 'empty';
    const levelsKey = hierarchyLevels.join(':');
    const comparisonKey = includeComparisonFields ? 'with-comparison' : 'no-comparison';
    return `${visualizationId}::${dataFingerprint}::${levelsKey}::${comparisonKey}`;
  }

  /**
   * Aggregate data by category (NEW METHOD)
   */
  private aggregateByCategory(
    data: AnalyticsDataPoint[],
    groupByFields: string[],
    metrics: string[],
    aggregation: 'sum' | 'avg' | 'min' | 'max' | 'count'
  ): AnalyticsDataPoint[] {
    if (!data || data.length === 0) return [];

    const grouped = new Map<string, AnalyticsDataPoint[]>();

    // Group data by the specified fields
    for (const record of data) {
      const groupKey = groupByFields.map(field => record[field] || 'Unknown').join('|');

      if (!grouped.has(groupKey)) {
        grouped.set(groupKey, []);
      }
      grouped.get(groupKey)!.push(record);
    }

    // Aggregate each group
    const result: AnalyticsDataPoint[] = [];

    for (const [groupKey, records] of Array.from(grouped.entries())) {
      const groupValues = groupKey.split('|');
      const aggregatedRecord: AnalyticsDataPoint = {};

      // Set groupBy field values
      groupByFields.forEach((field, index) => {
        aggregatedRecord[field] = groupValues[index] || 'Unknown';
      });

      // Aggregate each metric
      for (const metric of metrics) {
        const values = records
          .map(record => record[metric])
          .filter(val => val !== null && val !== undefined && !isNaN(Number(val)))
          .map(val => Number(val));

        if (values.length === 0) {
          aggregatedRecord[metric] = 0;
          continue;
        }

        switch (aggregation) {
          case 'sum':
            aggregatedRecord[metric] = values.reduce((sum, val) => sum + val, 0);
            break;
          case 'avg':
            aggregatedRecord[metric] = values.reduce((sum, val) => sum + val, 0) / values.length;
            break;
          case 'min':
            aggregatedRecord[metric] = Math.min(...values);
            break;
          case 'max':
            aggregatedRecord[metric] = Math.max(...values);
            break;
          case 'count':
            aggregatedRecord[metric] = values.length;
            break;
        }
      }

      // Add record count for debugging
      aggregatedRecord.recordCount = records.length;

      result.push(aggregatedRecord);
    }

    return result;
  }

  /**
   * Smart aggregation for tables with intelligent field type detection (NEW METHOD)
   */
  private aggregateTableByCategory(
    data: AnalyticsDataPoint[],
    groupByFields: string[],
    metrics: string[]
  ): AnalyticsDataPoint[] {
    if (!data || data.length === 0) return [];

    const grouped = new Map<string, AnalyticsDataPoint[]>();

    // Group data by the specified fields
    for (const record of data) {
      const groupKey = groupByFields.map(field => record[field] || 'Unknown').join('|');

      if (!grouped.has(groupKey)) {
        grouped.set(groupKey, []);
      }
      grouped.get(groupKey)!.push(record);
    }

    // Aggregate each group with smart field type detection
    const result: AnalyticsDataPoint[] = [];

    for (const [groupKey, records] of Array.from(grouped.entries())) {
      const groupValues = groupKey.split('|');
      const aggregatedRecord: AnalyticsDataPoint = {};

      // Set groupBy field values
      groupByFields.forEach((field, index) => {
        aggregatedRecord[field] = groupValues[index] || 'Unknown';
      });

      // Smart aggregation for each metric based on field name
      for (const metric of metrics) {
        const values = records
          .map(record => record[metric])
          .filter(val => val !== null && val !== undefined && !isNaN(Number(val)))
          .map(val => Number(val));

        if (values.length === 0) {
          aggregatedRecord[metric] = 0;
          continue;
        }

        // Determine aggregation method based on field name patterns
        const fieldName = metric.toLowerCase();
        let aggregation: 'sum' | 'avg' = 'sum';

        if (fieldName.includes('satisfaction') ||
          fieldName.includes('score') ||
          fieldName.includes('rating') ||
          fieldName.includes('avg') ||
          fieldName.includes('average') ||
          fieldName.includes('time') ||
          fieldName.includes('duration')) {
          aggregation = 'avg';
        }

        switch (aggregation) {
          case 'sum':
            aggregatedRecord[metric] = values.reduce((sum, val) => sum + val, 0);
            break;
          case 'avg':
            aggregatedRecord[metric] = Math.round((values.reduce((sum, val) => sum + val, 0) / values.length) * 100) / 100;
            break;
        }
      }

      // Add record count for debugging
      aggregatedRecord.recordCount = records.length;

      result.push(aggregatedRecord);
    }

    return result;
  }

  /**
   * Generate dynamic filter options from the actual dataset
   * This is called when switching to live data to populate filters with actual values
   */
  async generateDynamicFilterOptions(
    dataSetId: string,
    fieldName: string,
    hierarchyConfig?: any
  ): Promise<Array<{ value: any; label: string; children?: any[] }>> {
    return this.dataSetManager.generateDynamicFilterOptions(dataSetId, fieldName, hierarchyConfig);
  }
} 